import { getGlobalSentry } from './init'

/**
 * 包装异步函数，自动捕获错误并上报到 Sentry
 * @param fn 要包装的异步函数
 * @param context 额外的上下文信息
 * @returns 包装后的函数
 */
export function withSentryErrorHandling<T extends any[], R>(
  fn: (...args: T) => Promise<R>,
  context?: Record<string, any>,
): (...args: T) => Promise<R> {
  return async (...args: T): Promise<R> => {
    try {
      return await fn(...args)
    }
    catch (error) {
      const sentry = getGlobalSentry()
      if (sentry) {
        sentry.captureException(error as Error, {
          context: 'async_function_error',
          functionName: fn.name || 'anonymous',
          arguments: args,
          timestamp: new Date().toISOString(),
          ...context,
        })
      }
      throw error
    }
  }
}

/**
 * 包装异步函数，静默捕获错误（不抛出，返回默认值）
 * @param fn 要包装的异步函数
 * @param fallback 错误时的默认返回值
 * @param context 额外的上下文信息
 * @returns 包装后的函数
 */
export function withSilentErrorHandling<T extends any[], R>(
  fn: (...args: T) => Promise<R>,
  fallback: R,
  context?: Record<string, any>,
): (...args: T) => Promise<R> {
  return async (...args: T): Promise<R> => {
    try {
      return await fn(...args)
    }
    catch (error) {
      const sentry = getGlobalSentry()
      if (sentry) {
        sentry.captureException(error as Error, {
          context: 'async_function_error_silent',
          functionName: fn.name || 'anonymous',
          arguments: args,
          fallback,
          timestamp: new Date().toISOString(),
          ...context,
        })
      }
      return fallback
    }
  }
}

/**
 * 包装异步函数，支持重试机制
 * @param fn 要包装的异步函数
 * @param maxRetries 最大重试次数
 * @param delay 重试间隔（毫秒）
 * @param context 额外的上下文信息
 * @returns 包装后的函数
 */
export function withRetry<T extends any[], R>(
  fn: (...args: T) => Promise<R>,
  maxRetries: number = 3,
  delay: number = 1000,
  context?: Record<string, any>,
): (...args: T) => Promise<R> {
  return async (...args: T): Promise<R> => {
    let lastError: Error = new Error('Unknown error occurred')

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await fn(...args)
      }
      catch (error) {
        lastError = error as Error

        if (attempt === maxRetries) {
          // 最后一次尝试失败，上报错误
          const sentry = getGlobalSentry()
          if (sentry) {
            sentry.captureException(error as Error, {
              context: 'async_function_error_with_retry',
              functionName: fn.name || 'anonymous',
              arguments: args,
              attempt,
              maxRetries,
              timestamp: new Date().toISOString(),
              ...context,
            })
          }
          throw error instanceof Error ? error : new Error(String(error))
        }

        // 等待后重试
        await new Promise(resolve => setTimeout(resolve, delay * attempt))
      }
    }

    throw lastError instanceof Error ? lastError : new Error(String(lastError))
  }
}

/**
 * 包装异步函数，支持超时控制
 * @param fn 要包装的异步函数
 * @param timeout 超时时间（毫秒）
 * @param context 额外的上下文信息
 * @returns 包装后的函数
 */
export function withTimeout<T extends any[], R>(
  fn: (...args: T) => Promise<R>,
  timeout: number = 5000,
  context?: Record<string, any>,
): (...args: T) => Promise<R> {
  return async (...args: T): Promise<R> => {
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Function execution timed out after ${timeout}ms`))
      }, timeout)
    })

    try {
      return await Promise.race([fn(...args), timeoutPromise])
    }
    catch (error) {
      const sentry = getGlobalSentry()
      if (sentry) {
        sentry.captureException(error as Error, {
          context: 'async_function_error_timeout',
          functionName: fn.name || 'anonymous',
          arguments: args,
          timeout,
          timestamp: new Date().toISOString(),
          ...context,
        })
      }
      throw error instanceof Error ? error : new Error(String(error))
    }
  }
}

/**
 * 组合多个包装器
 * @param fn 要包装的异步函数
 * @param wrappers 包装器数组
 * @returns 包装后的函数
 */
export function composeWrappers<T extends any[], R>(
  fn: (...args: T) => Promise<R>,
  ...wrappers: Array<(fn: (...args: T) => Promise<R>) => (...args: T) => Promise<R>>
): (...args: T) => Promise<R> {
  return wrappers.reduce((wrappedFn, wrapper) => wrapper(wrappedFn), fn)
}
